import React, { useState, useEffect, useRef } from "react";

// Model Layout Component - Trends
const playBounce = (event, state) => {
  const el = event.currentTarget;
  el.classList.remove("map-hover-enter", "map-hover-leave");
  void el.offsetWidth;
  el.classList.add(state);
};

const bounceHandlers = {
  onMouseEnter: (e) => playBounce(e, "map-hover-enter"),
  onMouseLeave: (e) => playBounce(e, "map-hover-leave"),
};

const trendLayers = [
  {
    id: "tm1",
    src: "/assets/svgs/trends-svgs/mobile-svgs/tm1.svg",
    alt: "Ageing population bars",
    style: { right: "-41%", top: "30%" },
    static: true,
  },
  {
    id: "tm2",
    src: "/assets/svgs/trends-svgs/mobile-svgs/tm2.svg",
    alt: "Green care bars",
    style: { left: "75%", top: "15%" },
  },
  {
    id: "tm3",
    src: "/assets/svgs/trends-svgs/mobile-svgs/tm3.svg",
    alt: "Assistive devices illustration",
    style: { left: "18.35%", top: "16.85%" },
  },
  {
    id: "tm4",
    src: "/assets/svgs/trends-svgs/mobile-svgs/tm4.svg",
    alt: "Pink care bars",
    style: { left: "91%", top: "71.65%" },
  },
  {
    id: "tm5",
    src: "/assets/svgs/trends-svgs/mobile-svgs/tm5.svg",
    alt: "Growing ageing population label",
    style: { left: "50%", top: "65%" },
  },
  {
    id: "tm6",
    src: "/assets/svgs/trends-svgs/mobile-svgs/tm6.svg",
    alt: "Fertility and births trend",
    style: { left: "30.2%", top: "41%" },
  },
];

const TrendsMobileModel = ({
  className = "",
  showTooltips = true,
  isParentLoading = false,
  isPopUpOpen = false,
}) => {
  const [shouldJiggle, setShouldJiggle] = useState(false);
  const jiggleTriggeredRef = useRef(false);

  useEffect(() => {
    if (!isParentLoading && !isPopUpOpen && !jiggleTriggeredRef.current) {
      jiggleTriggeredRef.current = true;
      const timer = setTimeout(() => {
        setShouldJiggle(true);
        const stopTimer = setTimeout(() => {
          setShouldJiggle(false);
        }, 1500);
        return () => clearTimeout(stopTimer);
      }, 600); // Starts slightly after loader/popup clears
      return () => clearTimeout(timer);
    }
  }, [isParentLoading, isPopUpOpen]);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const getScrollTarget = (layerId) => {
    const scrollMap = {
      tm2: "decline-paid-care-workforce",
      tm3: "person-centered-care-caregiver-centered-care",
      tm4: "technology-supporting-caregiving",
      tm5: "changing-family-structures",
      tm6: "growing-aging-population",
    };
    return scrollMap[layerId];
  };

  return (
    <main className="relative pt-5">
      <section className="p-2">
        <div
          className={`relative mx-auto aspect-[4/8] max-w-[500px] ${className}`}
        >
          {trendLayers.map((layer) => {
            const interactive = !layer.static;
            const hoverTiming = {
              "--map-seq-name": "mapSeqReveal",
              "--map-seq-duration": "1200ms",
              "--map-seq-delay": "0ms",
            };

            const tooltipPos = (() => {
              switch (layer.tooltipPlacement) {
                case "left":
                  return "left-full top-1/2 -translate-y-1/2 ml-2";
                case "right":
                  return "right-full top-[14%] -translate-y-1/2 mr-2";
                case "top":
                  return "bottom-[80%] left-[75%] -translate-x-1/2 -mt-2";
                default:
                  return "top-full left-1/2 -translate-x-1/2 mt-2";
              }
            })();

            const scrollTarget = getScrollTarget(layer.id);
            const handleClick = scrollTarget
              ? () => scrollToSection(scrollTarget)
              : undefined;

            return (
              <div
                key={layer.id}
                className={`absolute -translate-x-1/2 -translate-y-1/2 ${
                  interactive ? "group" : ""
                }`}
                style={layer.style}
              >
                <div className="relative">
                  <div
                    className={
                      interactive && shouldJiggle ? "animate-jiggle" : ""
                    }
                  >
                    <img
                      src={layer.src}
                      alt={layer.alt}
                      // loading="lazy"
                      className={`select-none max-w-max object-contain ${
                        interactive
                          ? "map-hover-bounce hover:cursor-pointer pointer-events-auto map-seq"
                          : "pointer-events-none"
                      }`}
                      style={interactive ? { ...hoverTiming } : undefined}
                      {...(interactive ? bounceHandlers : {})}
                      {...(handleClick ? { onClick: handleClick } : {})}
                    />
                  </div>
                  {interactive &&
                    showTooltips &&
                    Array.isArray(layer.tooltips) &&
                    layer.tooltips.map((tip, idx) => {
                      const offset = idx * 44;
                      const placement = layer.tooltipPlacement;
                      const transform =
                        placement === "left" || placement === "right"
                          ? `translateY(calc(-50% + ${offset}px))`
                          : `translateX(-50%) translateY(${offset}px)`;

                      return (
                        <span
                          key={`${layer.id}-tip-${idx}`}
                          className={`pointer-events-none z-50 absolute rounded-md bg-[#FEFBF6] p-2 text-[11px] font-normal text-black text-nowrap opacity-0 transition-opacity duration-200 group-hover:opacity-100 ${tooltipPos}`}
                          style={{ transform }}
                        >
                          {tip}
                        </span>
                      );
                    })}
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </main>
  );
};

export default TrendsMobileModel;
